import { z } from 'zod';
import type { SynergyFigure } from '../../synergy-risk';
import type { AskViewRecoveryOutcome } from './recovery-outcome-payload';
import type { AskViewAddress, AskViewDecisionState } from './view-payload';

export const decisionAvailabilitySchema = z.enum(['available', 'none', 'not_available']);
export const decisionScopeCoverageSchema = z.enum(['complete', 'commitments_only']);
export type AskViewDecisionAvailability = z.infer<typeof decisionAvailabilitySchema>;
export type AskViewDecisionScopeCoverage = z.infer<typeof decisionScopeCoverageSchema>;
export type AskViewDecisionFinance = Readonly<{
  dependencyProgress?: Readonly<{
    plan: SynergyFigure | null;
    reported: SynergyFigure | null;
    asOf: string;
    note: string;
  }>;
  arrivedAsOf?: string;
  commitmentLabel?: string;
  commitmentValue?: SynergyFigure | null;
}>;
export const RECOVERY_MEASUREMENT_LABELS = {
  awaiting: 'Next recovery check {date}',
  // Beat 8: a checkpoint reading, not the decision report, so it never reads as "not reported".
  due: 'The {date} checkpoint has passed; the first recovery result is not in yet',
  checkpointMissing: 'Not yet measured · first check date not recorded',
  verified: 'Verified', of: 'of', reported: 'reported',
} as const;

export const recoveryMeasurementSchema = z.discriminatedUnion('state', [
  z.object({ state: z.literal('measured'), value: z.unknown() }),
  z.object({ state: z.enum(['not_yet_measured', 'check_due']),
    firstCheck: z.object({ date: z.string().date(), label: z.string().min(1) }) }),
  z.object({ state: z.literal('no_decision') }),
  z.object({ state: z.literal('not_available'), reason: z.literal('checkpoint_missing') }),
]);
export type AskViewRecoveryMeasurement = Readonly<
  Exclude<z.infer<typeof recoveryMeasurementSchema>, { state: 'measured' }>
  | { state: 'measured'; value: AskViewRecoveryOutcome }>;

export function recoveryMeasurementText(value: AskViewRecoveryMeasurement): string | null {
  if (value.state === 'measured') return value.value.statusWord;
  if (value.state === 'no_decision') return null;
  if (value.state === 'not_available') return RECOVERY_MEASUREMENT_LABELS.checkpointMissing;
  return RECOVERY_MEASUREMENT_LABELS[value.state === 'check_due' ? 'due' : 'awaiting']
    .replace('{date}', value.firstCheck.label);
}

export const decisionAccountabilitySchema = z.object({
  financialBasis: z.literal('run_rate'),
  planVersion: z.number().int().positive(),
  snapshotId: z.string().nullable(),
  observedOn: z.string().date().nullable(),
  recordedAt: z.string().datetime({ offset: true }).nullable(),
  checkpoint: z.discriminatedUnion('state', [
    z.object({ state: z.literal('available'), date: z.string().date(), label: z.string() }),
    z.object({ state: z.literal('not_available') }),
  ]),
  rankingBasis: z.enum(['remaining_value_at_stake', 'value_at_stake', 'not_available']),
  sourceArtifactRefs: z.array(z.string()).optional(),
  measurement: recoveryMeasurementSchema.optional(),
});
export type AskViewDecisionAccountability = Readonly<
  Omit<z.infer<typeof decisionAccountabilitySchema>, 'measurement'> & {
    measurement?: AskViewRecoveryMeasurement;
  }>;

/** Transport metadata only. The existing figure reader preserves the original value binding. */
export const publishedActionMetadataSchema = z.object({
  owner: z.string().nullable(),
  dueOn: z.string().date().nullable(),
  dueLabel: z.string().nullable(),
  overdue: z.boolean().nullable(),
  basis: z.string().nullable(),
  basisLabel: z.string().nullable(),
  source: z.object({ datasetRef: z.string(), revisionId: z.string(), rowKey: z.string().nullable() }),
  ownerInActionText: z.boolean().optional(),
  valueInActionText: z.boolean().optional(),
});
export type AskViewPublishedAction = Readonly<z.infer<typeof publishedActionMetadataSchema> & {
  value: SynergyFigure | null;
}>;

export const operatingReviewMetadataSchema = z.object({
  subject: z.string().min(1).optional(),
  asOf: z.string().date(),
  financialBasis: z.literal('run_rate'),
  savedInvestigation: z.object({
    state: z.literal('not_available'), reason: z.literal('contract_pending'),
  }),
  previousReview: z.object({
    state: z.literal('not_available'), reason: z.literal('not_connected'),
  }),
  recoveryOutcome: z.discriminatedUnion('state', [
    z.object({ state: z.literal('not_available'), reason: z.enum(['not_linked', 'checkpoint_missing']) }),
    z.object({ state: z.literal('not_yet_measured'),
      firstCheck: z.object({ date: z.string().date(), label: z.string().min(1) }) }),
    z.object({ state: z.literal('check_due'),
      firstCheck: z.object({ date: z.string().date(), label: z.string().min(1) }) }),
    z.object({ state: z.literal('available'), value: z.unknown() }),
  ]),
});
export type AskViewOperatingReview = Readonly<
  Omit<z.infer<typeof operatingReviewMetadataSchema>, 'recoveryOutcome'> & {
    recoveryOutcome: Readonly<{ state: 'not_available'; reason: 'not_linked' | 'checkpoint_missing' }>
      | Readonly<{ state: 'not_yet_measured' | 'check_due'; firstCheck: Readonly<{ date: string; label: string }> }>
      | Readonly<{ state: 'available'; value: AskViewRecoveryOutcome }>;
  }
>;

/** Additive metadata keeps previously published Home payloads readable. */
export type AskViewHomeDecision = AskViewDecisionFinance & Readonly<{
  decisionId?: string;
  ageText?: string;
  title: string;
  owner: string | null;
  daysOpen: number;
  state: Exclude<AskViewDecisionState, 'closed'>;
  statusWord?: string;
  statusDetail?: string | null;
  selectionReason?: string;
  statusText: string;
  next: string;
  why: string;
  /** Server-authored explanation from pin-guarded cause sizing; omitted on refusal. */
  causeWhy?: string;
  protectedValue: SynergyFigure | null;
  arrivedValue?: SynergyFigure | null;
  accountability?: AskViewDecisionAccountability;
  publishedAction?: AskViewPublishedAction | null;
  address: AskViewAddress;
  moveNote?: string;
}>;
